/**
 * Ticket sales campaigns — a time-boxed national discount on every workshop ticket.
 *
 * The rules here are the single definition every surface uses: the staff list's
 * status badge, the booking site's discounted price, the checkout charge and the
 * confirmation all call these functions rather than restating them.
 */

export const TicketSalesCampaignDiscountType = {
  PERCENT: 'PERCENT',
  FLAT: 'FLAT',
} as const;
export type TicketSalesCampaignDiscountType =
  (typeof TicketSalesCampaignDiscountType)[keyof typeof TicketSalesCampaignDiscountType];

export const TicketSalesCampaignStatus = {
  LIVE: 'live',
  SCHEDULED: 'scheduled',
  DISABLED: 'disabled',
  ENDED: 'ended',
} as const;
export type TicketSalesCampaignStatus =
  (typeof TicketSalesCampaignStatus)[keyof typeof TicketSalesCampaignStatus];

export const CAMPAIGN_NAME_MAX_LENGTH = 120;
export const CAMPAIGN_DESCRIPTION_MAX_LENGTH = 80;
export const CAMPAIGN_PERCENT_MIN = 1;
export const CAMPAIGN_PERCENT_MAX = 100;

/** A Date, or the ISO string a Date becomes on the wire. */
type Instant = Date | string;

const ms = (instant: Instant) => new Date(instant).getTime();

export interface CampaignSchedule {
  startsAt: Instant;
  endsAt: Instant;
  enabled: boolean;
}

export interface CampaignDiscount {
  discountType: TicketSalesCampaignDiscountType;
  /** Whole percent (1–100) for PERCENT; cents for FLAT. */
  discountValue: number;
}

/** Ended beats disabled beats live beats scheduled, in that order. */
export function campaignStatus(campaign: CampaignSchedule, now: Date): TicketSalesCampaignStatus {
  const at = now.getTime();
  if (ms(campaign.endsAt) <= at) return TicketSalesCampaignStatus.ENDED;
  if (!campaign.enabled) return TicketSalesCampaignStatus.DISABLED;
  if (ms(campaign.startsAt) <= at) return TicketSalesCampaignStatus.LIVE;
  return TicketSalesCampaignStatus.SCHEDULED;
}

/**
 * The campaign that applies at `now`: of the live ones, the one created first.
 * Ties on creation time fall to the lower id so every caller picks the same one.
 */
export function pickActiveCampaign<T extends CampaignSchedule & { id: string; createdAt: Instant }>(
  campaigns: readonly T[],
  now: Date,
): T | null {
  const live = campaigns.filter((c) => campaignStatus(c, now) === TicketSalesCampaignStatus.LIVE);
  live.sort((a, b) => ms(a.createdAt) - ms(b.createdAt) || a.id.localeCompare(b.id));
  return live[0] ?? null;
}

export interface DiscountedUnitPrice {
  discountedUnitPriceCents: number;
  discountPerTicketCents: number;
}

export interface DiscountedAmount {
  discountCents: number;
  discountedCents: number;
}

/** A percent or flat discount off an amount, rounded to whole cents and never below $0. */
export function applyDiscount(
  amountCents: number,
  discount: CampaignDiscount | null,
): DiscountedAmount {
  const raw = !discount
    ? 0
    : discount.discountType === TicketSalesCampaignDiscountType.PERCENT
      ? Math.round((amountCents * discount.discountValue) / 100)
      : discount.discountValue;
  const discountCents = Math.min(Math.max(raw, 0), Math.max(amountCents, 0));
  return { discountCents, discountedCents: amountCents - discountCents };
}

export function applyCampaignToUnitPrice(
  unitPriceCents: number,
  campaign: CampaignDiscount | null,
): DiscountedUnitPrice {
  const { discountCents, discountedCents } = applyDiscount(unitPriceCents, campaign);
  return { discountedUnitPriceCents: discountedCents, discountPerTicketCents: discountCents };
}

export interface DiscountedOrder {
  /** List price times tickets. */
  listSubtotalCents: number;
  /** Total discount across every ticket — what `campaignDiscountCents` stores. */
  campaignDiscountCents: number;
  /** After the discount — what `subtotalCents` stores and tax is computed on. */
  subtotalCents: number;
}

/** An order's subtotal under a campaign. The discount is rounded per ticket, then multiplied. */
export function applyCampaignToOrder(
  unitPriceCents: number,
  ticketCount: number,
  campaign: CampaignDiscount | null,
): DiscountedOrder {
  const { discountedUnitPriceCents, discountPerTicketCents } = applyCampaignToUnitPrice(
    unitPriceCents,
    campaign,
  );
  return {
    listSubtotalCents: unitPriceCents * ticketCount,
    campaignDiscountCents: discountPerTicketCents * ticketCount,
    subtotalCents: discountedUnitPriceCents * ticketCount,
  };
}

/** "20% off" or "$10 off" ("$10.50 off" when there are cents). */
export function campaignDiscountLabel(campaign: CampaignDiscount): string {
  if (campaign.discountType === TicketSalesCampaignDiscountType.PERCENT) {
    return `${campaign.discountValue}% off`;
  }
  return `${formatCampaignDollars(campaign.discountValue)} off`;
}

// Built once: a formatter is costly to create and labels render per row.
const dollarsFormatter = (digits: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
const WHOLE_DOLLARS = dollarsFormatter(0);
const DOLLARS_AND_CENTS = dollarsFormatter(2);

/** "$10" for whole dollars, "$10.50" when there are cents. */
export function formatCampaignDollars(cents: number): string {
  return (cents % 100 === 0 ? WHOLE_DOLLARS : DOLLARS_AND_CENTS).format(cents / 100);
}

/**
 * The discount line on a receipt or order summary: "Fall Repotting Week · 20% off".
 * Takes the locked terms the backend holds, or a name and a label already
 * worked out, which is what the booking site receives.
 */
export function campaignDiscountLineLabel(
  campaign: LockedCampaignTerms | { name: string; discountLabel: string },
): string {
  const label =
    'discountLabel' in campaign ? campaign.discountLabel : campaignDiscountLabel(campaign);
  return `${campaign.name} · ${label}`;
}

/** The banner line on the booking site: the staff-written text, else "20% off every ticket". */
export function campaignBannerDescription(
  campaign: CampaignDiscount & { description: string | null },
): string {
  const written = campaign.description?.trim();
  return written ? written : `${campaignDiscountLabel(campaign)} every ticket`;
}

/**
 * A campaign's terms as locked onto a checkout or booking when the checkout
 * started. Re-pricing and receipts use these, never the live campaign, so a
 * later edit cannot change an order already under way. Works anywhere a
 * `CampaignDiscount` does, e.g. `campaignDiscountLabel(terms)`.
 */
export interface LockedCampaignTerms extends CampaignDiscount {
  name: string;
}

/** The snapshot columns on `checkout_sessions` and `bookings`. */
export interface CampaignTermsColumns {
  campaignName: string | null;
  campaignDiscountType: TicketSalesCampaignDiscountType | null;
  campaignDiscountValue: number | null;
}

/** The columns to write when a campaign is locked onto a checkout or booking. */
export function campaignTermsColumns(
  campaign: (CampaignDiscount & { name: string }) | null,
): CampaignTermsColumns {
  return {
    campaignName: campaign?.name ?? null,
    campaignDiscountType: campaign?.discountType ?? null,
    campaignDiscountValue: campaign?.discountValue ?? null,
  };
}

/** The terms a checkout or booking was locked to, or null when no campaign applied. */
export function lockedCampaignTerms(row: CampaignTermsColumns): LockedCampaignTerms | null {
  const { campaignName, campaignDiscountType, campaignDiscountValue } = row;
  if (campaignName === null || campaignDiscountType === null || campaignDiscountValue === null) {
    return null;
  }
  return {
    name: campaignName,
    discountType: campaignDiscountType,
    discountValue: campaignDiscountValue,
  };
}

/** What the booking site is told about the campaign that applies. */
export interface PublicTicketSalesCampaign {
  id: string;
  name: string;
  bannerDescription: string;
  discountType: TicketSalesCampaignDiscountType;
  discountValue: number;
  discountLabel: string;
  /** UTC ISO instant. */
  endsAt: string;
}

export function toPublicTicketSalesCampaign(
  campaign: CampaignDiscount & {
    id: string;
    name: string;
    description: string | null;
    endsAt: Instant;
  },
): PublicTicketSalesCampaign {
  return {
    id: campaign.id,
    name: campaign.name,
    bannerDescription: campaignBannerDescription(campaign),
    discountType: campaign.discountType,
    discountValue: campaign.discountValue,
    discountLabel: campaignDiscountLabel(campaign),
    endsAt: new Date(campaign.endsAt).toISOString(),
  };
}
